import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/services', label: t('nav.services') },
    { to: '/about', label: t('nav.about') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-slate-200/50 shadow-sm'
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <nav className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_ced1def1-5c79-41a6-a911-205351f73e31/artifacts/90ux9tsg_v...%202.PNG"
              alt="VSP Logo"
              className="h-10 w-10 object-contain"
            />
            <span className="text-xl font-semibold text-[#1D1D1F] tracking-tight font-['Plus_Jakarta_Sans']">
              VSP
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'text-[#007AFF]'
                    : 'text-[#1D1D1F] hover:text-[#007AFF]'
                }`}
                data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#1D1D1F] hover:text-[#007AFF] transition-colors"
              data-testid="language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>
            <Link to="/contact">
              <Button
                className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-6 py-2 text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
                data-testid="get-started-btn"
              >
                {t('nav.getStarted')}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-[#1D1D1F]"
              data-testid="language-toggle-mobile"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-[#1D1D1F]"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden bg-white rounded-2xl mt-2 shadow-lg border border-slate-100"
            >
              <div className="py-4 px-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      isActive(link.to)
                        ? 'bg-[#F5F5F7] text-[#007AFF]'
                        : 'text-[#1D1D1F] hover:bg-[#F5F5F7]'
                    }`}
                    data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-2">
                  <Link to="/contact" className="block">
                    <Button
                      className="w-full bg-[#1D1D1F] text-white hover:bg-black rounded-full py-3 text-base font-medium"
                      data-testid="mobile-get-started-btn"
                    >
                      {t('nav.getStarted')}
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
